// src/features/monitoring/MonitoringPage.tsx
import { Activity, Clock3, Gauge, UsersRound } from 'lucide-react';
import { Card, EmptyState, formatPersianNumber } from '../../app/components/foundation';
import { ControlPlaneDataSurface, ControlPlaneMetricStrip, ControlPlanePage, ControlPlaneSection } from '../../app/components/foundation/control-plane';
import { VaniaContentSkeleton, VaniaLoadingSystem } from '../../app/components/loading';
import { MonitoringLiveSnapshot } from './components';
import { useMonitoring } from './hooks/useMonitoring';
import './styles/monitoring.css';

/**
 * Monitoring Foundation. Same layout pattern as CustomersPage/
 * AuditPage: a PageContainer shell, a feature-owned header, then
 * feature content below — RTL and Persian throughout, using only
 * existing tokens/primitives (Card, PageHeader) via
 * app/components/foundation.
 *
 * Every row here comes from features/monitoring/data/monitoring.mock.ts
 * via useMonitoringMock() — no monitoring endpoint exists yet.
 * useMonitoringMock is the seam a later phase swaps for a real API
 * hook (same shape, `{ services }`); MonitoringTable only depends on
 * the `services` prop, not on where it came from, so that swap won't
 * touch this file or MonitoringTable.tsx.
 */
export function MonitoringPage() {
  const { monitoring, loading, error } = useMonitoring();
  const initialLoading = loading && !monitoring;

  return (
    <ControlPlanePage
      className="vania-monitoring-control-plane-page"
      title=""
      summary={initialLoading ? <MonitoringMetricSkeleton /> : monitoring ? (
        <ControlPlaneMetricStrip
          items={[
            { label: 'وضعیت سیستم', value: monitoring.status, icon: <Activity size={16} />, tone: monitoring.status === 'healthy' ? 'success' : 'danger' },
            { label: 'مدت فعالیت', value: `${formatPersianNumber(monitoring.uptime_seconds)} ثانیه`, icon: <Gauge size={16} />, tone: 'primary' },
            { label: 'نشست‌های فعال', value: formatPersianNumber(monitoring.active_sessions), icon: <UsersRound size={16} />, tone: 'info' },
            { label: 'زمان بررسی', value: <span dir="ltr">{new Date(monitoring.checked_at * 1000).toLocaleString('fa-IR')}</span>, icon: <Clock3 size={16} />, tone: 'warning' },
          ]}
        />
      ) : undefined}
    >
      <ControlPlaneSection>
        <ControlPlaneDataSurface>
          {error && !loading ? <p className="vania-async-state vania-async-state-error" role="alert">{error}</p> : null}
          {initialLoading ? <MonitoringLoadingSkeleton /> : null}
          {!loading && !error && !monitoring ? <EmptyState message="اطلاعات مانیتورینگ برای نمایش وجود ندارد." /> : null}
          {monitoring ? <MonitoringLiveSnapshot monitoring={monitoring} serviceAction={<button type="button" disabled className="vania-monitoring-action-placeholder" title="در فاز بعدی فعال می‌شود">افزودن سرویس مانیتور</button>} /> : null}
        </ControlPlaneDataSurface>
      </ControlPlaneSection>
    </ControlPlanePage>
  );
}

function MonitoringMetricSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-4 vania-monitoring-summary-skeleton" aria-hidden="true">
      {Array.from({ length: 4 }, (_, index) => (
        <Card key={`monitoring-metric-summary-skeleton-${index}`}>
          <VaniaContentSkeleton style={{ width: '44%', height: '0.75rem' }} />
          <VaniaContentSkeleton style={{ width: '64%', height: '1.2rem' }} />
        </Card>
      ))}
    </div>
  );
}

function MonitoringLoadingSkeleton() {
  return (
    <VaniaLoadingSystem mode="content" className="vania-monitoring-loading-system" label="در حال بارگذاری وضعیت مانیتورینگ">
      <Card aria-hidden="true">
        <VaniaContentSkeleton style={{ width: '34%', height: '1rem' }} />
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {Array.from({ length: 3 }, (_, index) => (
            <div key={`monitoring-service-skeleton-${index}`}>
              <VaniaContentSkeleton style={{ width: '48%', height: '0.85rem' }} />
              <VaniaContentSkeleton style={{ width: '72%', height: '0.75rem' }} />
              <VaniaContentSkeleton style={{ width: '100%', height: '0.75rem' }} />
            </div>
          ))}
        </div>
      </Card>
      <Card aria-hidden="true">
        <VaniaContentSkeleton style={{ width: '32%', height: '1rem' }} />
        <VaniaContentSkeleton style={{ width: '78%', height: '0.85rem' }} />
      </Card>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2" aria-hidden="true">
        {Array.from({ length: 2 }, (_, index) => (
          <Card key={`monitoring-activity-skeleton-${index}`}>
            <VaniaContentSkeleton style={{ width: '38%', height: '1rem' }} />
            <VaniaContentSkeleton style={{ width: '52%', height: '0.9rem' }} />
          </Card>
        ))}
      </div>
    </VaniaLoadingSystem>
  );
}
